import { Injectable, OnDestroy } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar, MatSnackBarRef } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { TranslateService } from "@ngx-translate/core";
import { MessageLogDTO } from "app/data/schema/Communication/MessageLogDTO";
import { SiglaTemplateDTO } from "app/data/schema/Communication/SiglaTemplateDTO";
import { TemplateBodyTypeDTO } from "app/data/schema/Communication/TemplateBodyTypeDTO";
import { TemplateMessageTypeDTO } from "app/data/schema/Communication/TemplateMessageTypeDTO";
import { InviteValidationDTO } from "app/data/schema/Invite/InviteValidationDTO";
import { ApiUserCourseService } from "app/data/service/api-user-course.service";
import { ApiUsersService } from "app/data/service/api-users.service";
import { MessageLogService } from "app/data/service/Communication/message-log.service";
import { GenericNotificationComponent } from "app/shared/components/generic-notification/generic-notification.component";
import { UsersImportErrorsDialogComponent } from "app/shared/components/users-import-errors-dialog/users-import-errors-dialog.component";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AuthService } from "../auth.service";
import { SubscriptionService } from "../subscription.service";
import {
    CommunicationMessageComponent,
    notificationData
} from "./components/communication-message/communication-message.component";
import { locale as english } from "./i18n/en-US";
import { locale as spanish } from "./i18n/es-ES";
import { locale as portuguese } from "./i18n/pt-BR";

@Injectable({
    providedIn: "root"
})
export class CommunicationService implements OnDestroy {
    private interval: NodeJS.Timeout;
    private _unsubscribeAll: Subject<any>;

    msgsToShow: MessageLogDTO[] = [];
    msgOpened: MessageLogDTO | null;
    notificationTimeInMinutes = 5;
    getMessagesAgainInSec = 5;
    appMsgsStorageId = "APP_Notifications_Ids";

    constructor(
        private messageLogService: MessageLogService,
        private authService: AuthService,
        private snackBar: MatSnackBar,
        private translate: TranslateService,
        private router: Router,
        private subscriptionService: SubscriptionService,
        private dialog: MatDialog,
        private userService: ApiUsersService,
        private userCourseService: ApiUserCourseService,
        private fusetranslates: FuseTranslationLoaderService
    ) {
        this._unsubscribeAll = new Subject();
        this.fusetranslates.loadTranslations(english, spanish, portuguese);
    }
    ngOnDestroy(): void {
        this._unsubscribeAll.complete();
        this._unsubscribeAll.unsubscribe();
    }

    startVerify(): void {
        clearInterval(this.interval);
        this.interval = setInterval(() => {
            const userId = this.authService.getClaimsUserId();
            if (!userId) return;

            this.getMessages(userId);
            this.showMessages();
        }, 1000 * this.getMessagesAgainInSec);
    }

    getMessages(userId: string): void {
        if (this.msgOpened) return;

        this.messageLogService
            .list({
                onlyNotSent: true,
                userId: userId,
                orderBy: "createdat",
                find: "",
                skip: 0,
                take: 10,
                sortOrder: "asc",
                types: [TemplateMessageTypeDTO.AdminPush, TemplateMessageTypeDTO.UiPush],
                active: true
            })
            .subscribe((res) => {
                if (!res.data) return;
                res.data = res.data.filter((m) => m.template !== SiglaTemplateDTO.UserSaved);

                this.setAppMsg(res.data);
                this.msgsToShow = res.data?.filter((m) => m.tipo == TemplateMessageTypeDTO.AdminPush);
                this.showMessages();
            });
    }

    setAppMsg(msgs: MessageLogDTO[]): void {
        msgs = msgs?.filter((m) => m.tipo == TemplateMessageTypeDTO.UiPush) ?? [];
        if (!msgs || msgs.length == 0) return;

        let hasNew = false;
        const storaged = JSON.parse(localStorage.getItem(this.appMsgsStorageId)) ?? ([] as MessageLogDTO[]);
        msgs.forEach((msg) => {
            if (storaged?.find((m) => m.id == msg.id)) return;
            hasNew = true;
        });
        localStorage.setItem(this.appMsgsStorageId, JSON.stringify(msgs));

        if (hasNew) {
            this.msgsToShow.push({
                id: "app-notifications",
                criadoEm: null,
                destinatario: null,
                enviadoEm: null,
                mensagem: "",
                parameters: null,
                subscriptionId: null,
                template: SiglaTemplateDTO.APPNotifications,
                tipo: null,
                titulo: null,
                userId: null
            });
            this.showMessages();
        }
    }

    showAppNotifications(count: number, msgId: string): void {
        const snack = this.openNotfication(
            count > 1
                ? this.translate.instant("NOTIFICATION.APP_HAS_NOTIFICATIONS", { count: count })
                : this.translate.instant("NOTIFICATION.APP_HAS_NOTIFICATION"),
            this.translate.instant("NOTIFICATION.GO_TO_APP")
        );

        snack.onAction().subscribe(() => {
            window.open(
                "about:blank",
                "_blank"
            ).location.href = `https://${this.subscriptionService.currentSubscription.host}/app/home`;
        });

        snack.afterDismissed().subscribe(() => {
            this.removeMsg(msgId);
            this.msgOpened = null;
        });
    }

    showMessages(): void {
        if (!this.msgsToShow || this.msgsToShow.length == 0 || this.msgOpened) return;

        const msg = this.msgsToShow[0];
        this.msgOpened = msg;
        switch (msg.template) {
            case SiglaTemplateDTO.UserExportCompleted: {
                this.userService.getBulk(msg.parameters["Id"]).subscribe((res) => {
                    const snack = this.openNotfication(
                        this.translate.instant("NOTIFICATION.USER_EXPORT_COMPLETED"),
                        this.translate.instant("NOTIFICATION.DOWNLOAD")
                    );

                    snack.onAction().subscribe(() => {
                        window.open("about:blank", "_blank").location.href = res.outputUrl;
                    });

                    snack.afterDismissed().subscribe(() => this.markMessageAsSent(msg.id));
                });

                break;
            }
            case SiglaTemplateDTO.UserImportCompleted: {
                if (msg.parameters["Errors"]) {
                    const errors = JSON.parse(msg.parameters["Errors"]) as InviteValidationDTO[];
                    this.dialog
                        .open(UsersImportErrorsDialogComponent, {
                            width: "98vw",
                            data: { validations: errors }
                        })
                        .afterClosed()
                        .subscribe(() => {
                            this.markMessageAsSent(msg.id);
                        });

                    return;
                }

                const snack = this.openNotfication(this.translate.instant("NOTIFICATION.USER_IMPORT_SUCCESS"));
                snack.afterDismissed().subscribe(() => this.markMessageAsSent(msg.id));
                break;
            }
            case SiglaTemplateDTO.DOWNLOAD_CERTIFICATES: {
                this.userCourseService.getZipCertificatesUrl(msg.parameters["requestId"]).subscribe((res) => {
                    const snack = this.openNotfication(
                        this.translate.instant("NOTIFICATION.CERTIFICATE_EXPORT_COMPLETED"),
                        this.translate.instant("NOTIFICATION.DOWNLOAD")
                    );
                    snack.onAction().subscribe(() => {
                        window.open("about:blank", "_blank").location.href = res;
                    });

                    snack.afterDismissed().subscribe(() => this.markMessageAsSent(msg.id));
                });

                break;
            }
            case SiglaTemplateDTO.APPNotifications: {
                this.showAppNotifications(Number(msg.mensagem), msg.id);

                break;
            }
            default: {
                this.getMessageBody(msg.id);
                break;
            }
        }
    }

    getMessageBody(msgId: string): void {
        this.messageLogService.getMessageBody(msgId, this.translate.currentLang).subscribe((template) => {
            switch (template.templateBodyType) {
                case TemplateBodyTypeDTO.Text: {
                    const snack = this.openNotfication(template.templateBodyMessage);
                    snack
                        .afterDismissed()
                        .pipe(takeUntil(this._unsubscribeAll))
                        .subscribe(() => {
                            this.markMessageAsSent(msgId);
                        });
                    break;
                }
                case TemplateBodyTypeDTO.Html: {
                    const ref = this.dialog.open(GenericNotificationComponent, {
                        data: template.templateBodyMessage,
                        width: "1200px",
                        closeOnNavigation: false,
                        disableClose: true
                    });
                    ref.afterClosed()
                        .pipe(takeUntil(this._unsubscribeAll))
                        .subscribe(() => {
                            this.markMessageAsSent(msgId);
                        });
                    break;
                }
                default:
                    throw new Error(`template body type '${template.templateBodyType}' not supported`);
            }
        });
    }
    removeMsg(id: string): void {
        const i = this.msgsToShow.findIndex((m) => m.id == id);
        if (i < 0) return;

        this.msgsToShow.splice(i, 1);
        if (this.msgsToShow?.length > 0) {
            setTimeout(() => this.showMessages(), 500);
        }
    }

    markMessageAsSent(msgId: string): void {
        this.removeMsg(msgId);
        this.msgOpened = null;
        this.messageLogService.markMessageAsSent(msgId).subscribe();
    }

    openNotfication(msgTxt: string, action: string | null = null): MatSnackBarRef<CommunicationMessageComponent> {
        return this.snackBar.openFromComponent<CommunicationMessageComponent>(CommunicationMessageComponent, {
            data: { message: msgTxt, action: action } as notificationData,
            horizontalPosition: "center",
            verticalPosition: "top",
            duration: 1000 * 60 * this.notificationTimeInMinutes
        });
    }
}
